import { useCallback, useEffect, useMemo } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { useAppSelector, useAppStore } from "@/store"
import { benefitApi } from "./benefitApi"
import { benefitReadRuntime } from "./benefitReadBinding"
import { benefitReadKey } from "./benefitReadSlice"

const emptyNames: Record<string, string> = {}

/** One canonical server snapshot; catalogs remain shared, scoped and independently cached. */
export function useBenefitReadBridge(benefitId: string | undefined) {
  const store = useAppStore()
  const scopeKey = useAppSelector(state => state.staffSession.scopeKey)
  const resource = useMemo(() => scopeKey && benefitId ? { scopeKey, id: benefitId, kind: "benefit-application" as const } : null, [scopeKey, benefitId])
  const args = resource ? { scopeKey: resource.scopeKey, benefitId: resource.id } : skipToken
  const catalogArgs = scopeKey && benefitId ? { scopeKey } : skipToken
  // Only the runtime owns the subscription. Sections select this same entry.
  const workspace = benefitApi.endpoints.getBenefitWorkspace.useQueryState(args)
  const providers = benefitApi.useGetBenefitServiceProvidersQuery(catalogArgs)
  const vendors = benefitApi.useGetBenefitVendorsQuery(catalogArgs)
  const payments = benefitApi.useGetBenefitVendorPaymentOptionsQuery(catalogArgs)
  const energy = benefitApi.useGetBenefitEnergyServicesQuery(catalogArgs)
  const grants = benefitApi.useGetBenefitGrantProgramsQuery(catalogArgs)
  const freshness = useAppSelector(state => resource ? state.benefitReads.resources[benefitReadKey(resource)] : undefined)
  const runtime = benefitReadRuntime(store)
  useEffect(() => resource ? runtime.observe(resource) : undefined, [runtime, resource])
  // Compatibility callback deliberately ignores partial command DTOs. The same
  // coordinator handles all writes; a late GET cannot make old capabilities current.
  const refresh = useCallback(() => { if (resource) runtime.refresh(resource) }, [runtime, resource])
  const snapshot = freshness?.phase === "accessDenied" ? null : workspace.currentData
  const benefit = snapshot && snapshot.benefit.id === benefitId ? snapshot.benefit : null
  const current = freshness?.phase === "current" && !workspace.isFetching && !workspace.isError
  const catalogs = [providers, vendors, payments, energy, grants]
  const vendorNames = useMemo(() => ({ ...(vendors.currentData ?? emptyNames),
    ...Object.fromEntries((payments.currentData?.historical ?? []).map(vendor => [String(vendor.id), vendor.name])),
  }), [vendors.currentData, payments.currentData])
  return {
    benefit,
    refresh,
    current,
    readStamp: snapshot?.readStamp ?? null,
    workflow: current && snapshot?.workflow.status === "available" ? snapshot.workflow.value : null,
    loading: !scopeKey || (!benefit && (workspace.isUninitialized || workspace.isLoading || workspace.isFetching)) || catalogs.some(query => query.isLoading),
    error: !benefit && (workspace.isError || freshness?.phase === "accessDenied") ? "This benefit application is unavailable or access was denied." : null,
    refreshUnavailable: !!benefit && (workspace.isError || freshness?.phase === "unavailable"),
    catalogUnavailable: catalogs.some(query => query.isError),
    providerNames: providers.currentData ?? emptyNames,
    vendorNames,
    selectableVendors: payments.currentData?.selectable ?? [],
    selectableGrantPrograms: grants.currentData ?? [],
    selectableGrantProgramsLoadFailed: grants.isError,
    energyServiceOptions: energy.currentData ?? [],
  }
}
